import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./App.css";
import { ToastContainer } from "react-toastify";

// Screens & Components
import Login from "./Pages/Login/Login";
import Dashboard from "./Pages/Dashboard/Dashboard";
import NewDashboard from "./Pages/Dashboard/NewDashboard";
import DashboardContributeNow from "./Pages/Dashboard/DashboardContributeNow";
import DashboardCollabRequest from "./Pages/Dashboard/DashboardCollabRequest";
import DetailsView from "./Pages/DetailsView/DetailsView";
import CreateUser from "./Pages/Login/CreateUser";
import PaymentSuccess from "./Pages/Payment/PaymentSuccess";
import PaymentFailed from "./Pages/Payment/PaymentFailed";
import Contactus from "./Pages/Contactus/Contactus";
import Favorites from "./Pages/Favorites/Favorites";
import CreateAthleteProfile from "./Pages/CreateAthleteProfile/CreateAthleteProfile";

// Info Pages
import AboutUsComponent from "./Components/Informations/AboutUsComponent/AboutUsComponent";
import Transparency from "./Components/Informations/Transparency/Transparency";
import PrivacyPolicy from "./Components/Informations/PrivacyPolicy/PrivacyPolicy";
import ImpactAthleteJourney from "./Components/Informations/ImpactAthleteJourney/ImpactAthleteJourney";
import NILComplianceAndProcedures from "./Components/Informations/NILComplianceAndProcedures/NILComplianceAndProcedures";
import AddressingNilInequality from "./Components/Informations/AddressingNilInequality/AddressingNilInequality";
import OurMission from "./Components/Informations/OurMission/OurMission";
import NILSupportServicesAndFees from "./Components/Informations/NILSupportServicesAndFees/NILSupportServicesAndFees";

// Admin Screens
import AccountInformation from "./Pages/AdminScreens/AccountInformation/AccountInformation";
import ChangePassword from "./Pages/AdminScreens/ChangePassword/ChangePassword";
import DeleteScreen from "./Pages/AdminScreens/DeleteScreen/DeleteScreen";
import AdminDashboard from "./Pages/AdminScreens/AdminDashboard/AdminDashboard";

// Athlete Screen
import AthleteDashboard from "./Pages/AthleteDashboard/AthleteDashboard";

// Constants & ProtectedRoute
import {
  IMPACT_ATHELETE_JOURNEY,
  ABOUT_US,
  ACCOUNT_INFORMATION,
  ADMIN_DASHBOARD,
  CHANGE_PASSWORD,
  CONTACT_US,
  CREATE_USER,
  DASHBOARD,
  NEWDASHBOARD,
  DELETE_SCREEN,
  DETAILS_VIEW,
  ADDRESSING_NIL_INEQUALITY,
  NIL_SUPPORT_SERVICES_AND_FEES,
  TRANSPARENCY,
  FAVORITES,
  LOGIN,
  BUILDING_TRUST_IN_NIL,
  NIL_COMPLIANCE_AND_PROCEDURES,
  PAYMENT_FAILED,
  PAYMENT_SUCCESS,
  PRIVACY_POLICY,
} from "./Utils/Constants";

import ProtectedRoute from "./Utils/ProtectedRoute";
import { Auth0Provider } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import FanExperience from "./Pages/FanExperience/FanExperience";
import BrandPartnership from "./Pages/BrandPartnership/BrandPartnership";
import FanEngagementType from "./Pages/FanEngagementType/FanEngagementType";
import BrandPartnershipType from "./Components/BrandPartnershipType/BrandPartnershipType";
import AthleteProfile from "./Pages/AthleteProfile/AthleteProfile";

function Auth0ProviderWithNavigate({ children }) {
  const navigate = useNavigate();
  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || "/dashboard/start-your-brand-partnership/1/hawaii");
  };
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENTID}
      authorizationParams={{ redirect_uri: window.location.origin }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

function App() {
  return (
    <Router>
      <Auth0ProviderWithNavigate>
      <div className="App">
          <ToastContainer
            position="top-center"
            autoClose={1000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Routes>
            {/* Public Routes */}
            <Route path={LOGIN} element={<Login />} />
            <Route path="/" element={<NewDashboard />} />

            {/* Dashboard */}
            <Route path={DASHBOARD} element={<Dashboard />} />

            {/* ── Hawaii-locked routes (must come BEFORE the generic :sportId/:teamId routes) ── */}
            <Route
              path="/dashboard/start-your-fan-experience/:sportId/hawaii"
              element={<DashboardContributeNow />}
            />
            <Route
              path="/dashboard/start-your-brand-partnership/:sportId/hawaii"
              element={<DashboardCollabRequest />}
            />

            {/* Generic sport+team routes */}
            <Route
              path="/dashboard/start-your-fan-experience"
              element={<DashboardContributeNow />}
            />
            <Route
              path="/dashboard/start-your-fan-experience/:sportId/:teamId"
              element={<DashboardContributeNow />}
            />
            <Route
              path="/dashboard/start-your-brand-partnership"
              element={<DashboardCollabRequest />}
            />
            <Route
              path="/dashboard/start-your-brand-partnership/:sportId/:teamId"
              element={<DashboardCollabRequest />}
            />

            {/* ID-based Dashboard routes */}
            <Route path="/:teamId/:playerId" element={<Dashboard />} />
            <Route path="/:teamId" element={<Dashboard />} />

            {/* Details / engagement pages */}
            <Route path="/DetailsView/:playerId/:teamId" element={<DetailsView />} />
            <Route path="/FanExperience/:playerId/:teamId" element={<FanExperience />} />
            <Route path="/BrandPartnership/:playerId/:teamId" element={<BrandPartnership />} />
            <Route path="/FanEngagementType" element={<FanEngagementType />} />
            <Route path="/BrandPartnershipType" element={<BrandPartnershipType />} />
            <Route path="/AthleteProfile" element={<AthleteProfile />} />

            {/* Auth / Account */}
            <Route path={CREATE_USER} element={<CreateUser />} />

            {/* Contact & Favorites */}
            <Route path={CONTACT_US} element={<Contactus />} />
            <Route path={FAVORITES} element={<Favorites />} />

            {/* Payments */}
            <Route path={PAYMENT_SUCCESS} element={<PaymentSuccess />} />
            <Route path={PAYMENT_FAILED} element={<PaymentFailed />} />

            {/* Create Athlete Profile */}
            <Route path="/CreateAthleteProfile" element={<CreateAthleteProfile />} />

            {/* Info Pages */}
            <Route path={ABOUT_US} element={<AboutUsComponent />} />
            <Route path={TRANSPARENCY} element={<Transparency />} />
            <Route path={PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route path={IMPACT_ATHELETE_JOURNEY} element={<ImpactAthleteJourney />} />
            <Route path={NIL_COMPLIANCE_AND_PROCEDURES} element={<NILComplianceAndProcedures />} />
            <Route path={ADDRESSING_NIL_INEQUALITY} element={<AddressingNilInequality />} />
            <Route path={BUILDING_TRUST_IN_NIL} element={<OurMission />} />
            <Route path={NIL_SUPPORT_SERVICES_AND_FEES} element={<NILSupportServicesAndFees />} />

            {/* Admin / Protected */}
            <Route path={ACCOUNT_INFORMATION} element={<AccountInformation />} />
            <Route path={CHANGE_PASSWORD} element={<ChangePassword />} />
            <Route path={DELETE_SCREEN} element={<DeleteScreen />} />

            <Route
              path={ADMIN_DASHBOARD}
              element={
                <ProtectedRoute allowedRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/AthleteDashboard"
              element={
                <ProtectedRoute allowedRole="athlete">
                  <AthleteDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Auth0ProviderWithNavigate>
    </Router>
  );
}

export default App;